/**
 * Aba Regua: funis, regras do motor, horario permitido e toques.
 *
 * Decisoes nao obvias:
 *  - FUNIL SO DEPOIS DO FATO. Cada lista de toques abre com o MARCO ZERO (o
 *    abandono ou o vencimento) e uma nota diz por que nao existe toque antes:
 *    quem ainda esta comprando ou ainda e assinante nao saiu, e sem saida nao
 *    ha recuperacao nem comissao. Um toque com atraso menor que o minimo vem
 *    marcado "antes do fato", travado e conta como erro -- o mock nunca gera
 *    isso, mas a tela precisa saber recusar quando o backend permitir editar.
 *  - Espera de 30 min (RF04) e teto de 2 ligacoes (RF08) NAO sao campos: sao
 *    cartoes de leitura com o selo "Fixo". Um input desabilitado sugeriria que
 *    "alguem" pode mudar; aqui a regra e do produto, e o texto diz por que. O
 *    terceiro cartao (comissao e estorno) e do CONTRATO: a empresa nao altera
 *    por aqui, mas precisa ver a regra que define o que paga.
 *  - Desligar um funil pede CONFIRMACAO com o impacto: quantas pessoas estao
 *    na regua agora e deixariam de ser contatadas, quanto esta parado nesses
 *    carrinhos e quanto o funil recuperou nos ultimos 30 dias (com a variacao
 *    contra os 30 anteriores). Desligar um toque nao pede modal (e reversivel
 *    ate salvar), mas mostra na hora quem esta esperando por ele e para onde
 *    essas pessoas vao.
 *  - Os toques sao SOMENTE LEITURA fora do liga/desliga. Atraso, canal e copy
 *    moram na tela do funil, que ja tem o editor com previa do aparelho; dois
 *    editores para o mesmo texto divergem em semanas.
 *  - "Salvar na previa" devolve a fatia para a pagina (`onSaved`): e o que a
 *    prontidao le. Ids de campo estaveis (`htmlFor`) para a lista de erros.
 */

import { useState } from 'react';
import { Badge, Button, Checkbox, Field, Footnote, Icon, Input, LinkButton, MetricLine, Modal, Panel, SectionCard, Toggle } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatCountCompact, formatPercentFromBps } from '../../../lib/format';
import type { CompanySummary, FunnelKind } from '../../../types';
import type { CadenceSettings, ContactWindow, FunnelSettings, RefundPolicy, TouchSummary, WebhookProvider, Weekday } from '../types';
import { useRascunho } from '../useRascunho';
import {
  CANAL_LABEL,
  FATO_DO_FUNIL,
  FUNIL_INFO,
  NOTA_DEPOIS_DO_FATO,
  REFUND_POLICY_INFO,
  WEBHOOK_PROVIDER_LABEL,
  WEEKDAYS,
  descricaoDoAtraso,
  formatarHoras,
  minutosDoHorario,
  rotuloDoAtraso,
  variar,
} from '../vocabulario';
import { BarraSalvar, type ErroDeCampo } from './BarraSalvar';

const ID_INICIO = 'cfg-horario-inicio';
const ID_FIM = 'cfg-horario-fim';
const ID_DIAS = 'cfg-horario-dias';
const JANELA_CURTA_MIN = 4 * 60;
const TODOS_OS_DIAS: readonly Weekday[] = WEEKDAYS.map((d) => d.value);
const DIAS_UTEIS: readonly Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri'];

interface ErrosDaJanela {
  readonly start: string | null;
  readonly end: string | null;
  readonly weekdays: string | null;
  /** Minutos por dia quando inicio e fim sao validos. */
  readonly minutosPorDia: number | null;
}

function validarJanela(janela: ContactWindow): ErrosDaJanela {
  const inicio = minutosDoHorario(janela.start);
  const fim = minutosDoHorario(janela.end);
  const start = inicio === null ? 'Informe o horário de início.' : null;
  let end = fim === null ? 'Informe o horário de fim.' : null;
  if (end === null && inicio !== null && fim !== null && inicio >= fim) {
    end = 'O fim precisa ser depois do início.';
  }
  const weekdays = janela.weekdays.length === 0 ? 'Escolha ao menos um dia da semana.' : null;
  const minutosPorDia = start === null && end === null && inicio !== null && fim !== null ? fim - inicio : null;
  return { start, end, weekdays, minutosPorDia };
}

/** Mantem os dias na ordem da semana, independentemente da ordem dos cliques. */
function ordenarDias(dias: readonly Weekday[]): readonly Weekday[] {
  const escolhidos = new Set(dias);
  return TODOS_OS_DIAS.filter((v) => escolhidos.has(v));
}

function mesmosDias(a: readonly Weekday[], b: readonly Weekday[]): boolean {
  return a.length === b.length && a.every((d) => b.includes(d));
}

function antesDoFato(kind: FunnelKind, toque: TouchSummary): boolean {
  return toque.delayMinutes < FATO_DO_FUNIL[kind].minutos;
}

/** Proximo toque LIGADO depois deste; `null` quando ele e o ultimo ligado. */
function proximoLigado(funil: FunnelSettings, toque: TouchSummary): TouchSummary | null {
  return funil.touches.filter((t) => t.enabled && t.order > toque.order).sort((a, b) => a.order - b.order)[0] ?? null;
}

/** Variacao dos 30 dias contra os 30 anteriores: glifo + texto, nunca so cor. */
function VariacaoTexto({ atual, anterior }: { readonly atual: number; readonly anterior: number }): JSX.Element {
  const v = variar(atual, anterior);
  const titulo = 'Comparado com os 30 dias imediatamente anteriores';
  if (v.direcao === 'sem_base') {
    return (
      <span className="text-ink-subtle" title={titulo}>
        sem base anterior
      </span>
    );
  }
  if (v.direcao === 'igual') {
    return (
      <span className="text-ink-subtle" title={titulo}>
        <span aria-hidden="true">=</span> igual aos 30 dias anteriores
      </span>
    );
  }
  const alta = v.direcao === 'alta';
  return (
    <span className={`tabular-nums ${alta ? 'text-status-good' : 'text-status-critical'}`} title={titulo}>
      <span aria-hidden="true">{alta ? '▲' : '▼'}</span> <span className="sr-only">{alta ? 'alta' : 'queda'} de </span>
      {v.relativaBps === null ? `${formatBRLFromCents(Math.abs(v.absoluta))} (antes: ${formatBRLFromCents(0)})` : `${formatPercentFromBps(Math.abs(v.relativaBps))} vs. 30 dias anteriores`}
    </span>
  );
}

function RegraFixa({
  icone,
  titulo,
  selo,
  valor,
  unidade,
  texto,
}: {
  readonly icone: 'relogio' | 'telefone' | 'comissao';
  readonly titulo: string;
  readonly selo: string;
  readonly valor: string;
  readonly unidade: string;
  readonly texto: string;
}): JSX.Element {
  return (
    <Panel tone="elevated" className="flex flex-col gap-2 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-semibold text-ink">
          <Icon name={icone} size="sm" />
          {titulo}
        </p>
        <Badge tone="neutral" icon="escudo">
          {selo}
        </Badge>
      </div>
      <p className="text-2xl font-semibold tabular-nums text-ink">
        {valor} <span className="text-base font-medium text-ink-muted">{unidade}</span>
      </p>
      <p className="text-sm text-ink-muted">{texto}</p>
    </Panel>
  );
}

export function AbaRegua({
  base,
  company,
  provedoresConectados,
  refundPolicy,
  onSaved,
}: {
  readonly base: CadenceSettings;
  readonly company: CompanySummary;
  /** Provedores com webhook conectado (da configuracao efetiva de Integracoes). */
  readonly provedoresConectados: readonly WebhookProvider[];
  readonly refundPolicy: RefundPolicy;
  readonly onSaved: (salvo: CadenceSettings) => void;
}): JSX.Element {
  const rascunho = useRascunho(base);
  const [tentouSalvar, setTentouSalvar] = useState(false);
  const [desligando, setDesligando] = useState<FunnelSettings | null>(null);

  const { draft } = rascunho;
  const janela = validarJanela(draft.contactWindow);
  const toquesAntesDoFato = draft.funnels.flatMap((f) => f.touches.filter((t) => t.enabled && antesDoFato(f.kind, t)).map((t) => ({ kind: f.kind, toque: t })));

  const erros: readonly ErroDeCampo[] = [
    janela.start === null ? null : { id: ID_INICIO, campo: 'Início', mensagem: janela.start },
    janela.end === null ? null : { id: ID_FIM, campo: 'Fim', mensagem: janela.end },
    janela.weekdays === null ? null : { id: ID_DIAS, campo: 'Dias da semana', mensagem: janela.weekdays },
    ...toquesAntesDoFato.map((x) => ({
      id: `cfg-toque-${x.kind}-${x.toque.id}`,
      campo: `${FUNIL_INFO[x.kind].label} · ${rotuloDoAtraso(x.kind, x.toque.delayMinutes)}`,
      mensagem: `Toque antes do fato (mínimo ${FATO_DO_FUNIL[x.kind].texto}). Desligue-o ou ajuste o atraso na tela do funil.`,
    })),
  ].filter((e): e is ErroDeCampo => e !== null);
  const temErro = erros.length > 0;
  const janelaCurta = janela.minutosPorDia !== null && janela.minutosPorDia < JANELA_CURTA_MIN;

  const definirFunil = (kind: FunnelKind, enabled: boolean): void => {
    rascunho.setDraft((d) => ({ ...d, funnels: d.funnels.map((f) => (f.kind === kind ? { ...f, enabled } : f)) }));
  };

  const pedirDesligar = (funil: FunnelSettings, enabled: boolean): void => {
    if (enabled) {
      definirFunil(funil.kind, true);
      return;
    }
    setDesligando(funil);
  };

  const confirmarDesligar = (): void => {
    if (desligando !== null) {
      definirFunil(desligando.kind, false);
      setDesligando(null);
    }
  };

  const definirToque = (kind: FunnelKind, id: string, enabled: boolean): void => {
    rascunho.setDraft((d) => ({
      ...d,
      funnels: d.funnels.map((f) => (f.kind !== kind ? f : { ...f, touches: f.touches.map((t) => (t.id === id ? { ...t, enabled } : t)) })),
    }));
  };

  const definirJanela = (parcial: Partial<ContactWindow>): void => {
    rascunho.setDraft((d) => ({ ...d, contactWindow: { ...d.contactWindow, ...parcial } }));
  };

  const definirDia = (dia: Weekday, ligado: boolean): void => {
    const atuais = draft.contactWindow.weekdays;
    definirJanela({ weekdays: ligado ? ordenarDias([...atuais, dia]) : atuais.filter((d) => d !== dia) });
  };

  const salvar = (): void => {
    setTentouSalvar(true);
    if (!temErro) {
      rascunho.salvar();
      onSaved(draft);
      setTentouSalvar(false);
    }
  };

  const descartar = (): void => {
    setTentouSalvar(false);
    rascunho.descartar();
  };

  const erroVisivel = (mensagem: string | null): string | null => (tentouSalvar ? mensagem : null);
  const diasEscolhidos = draft.contactWindow.weekdays.length;
  const ligados = draft.funnels.filter((f) => f.enabled).length;

  return (
    <div className="flex flex-col gap-6">
      <SectionCard
        id="funis-ativos"
        title="Funis"
        icon="funil"
        description="Cada funil tem a própria régua e só recebe quem já saiu: abandonou o checkout ou deixou o plano vencer. Desligar um funil para os toques na hora."
        aside={
          <Badge tone={ligados > 0 ? 'good' : 'critical'} dot>
            {formatCount(ligados)} de {formatCount(draft.funnels.length)} ligados
          </Badge>
        }
      >
        <ul className="flex flex-col gap-3">
          {draft.funnels.map((funil) => {
            const info = FUNIL_INFO[funil.kind];
            const baseDoFunil = base.funnels.find((f) => f.kind === funil.kind);
            const vaiDesligar = baseDoFunil?.enabled === true && !funil.enabled;
            const vaiLigar = baseDoFunil?.enabled === false && funil.enabled;
            return (
              <li key={funil.kind}>
                <Panel tone="elevated" className="flex flex-col gap-3 p-4">
                  <Toggle
                    checked={funil.enabled}
                    onChange={(v) => pedirDesligar(funil, v)}
                    label={
                      <span className="flex flex-wrap items-center gap-2">
                        <Icon name={info.icon} size="sm" />
                        {info.label}
                        {vaiDesligar ? (
                          <Badge tone="warning" dot>
                            Desliga ao salvar
                          </Badge>
                        ) : null}
                        {vaiLigar ? (
                          <Badge tone="good" dot>
                            Liga ao salvar
                          </Badge>
                        ) : null}
                      </span>
                    }
                    description={info.feed}
                  />
                  <dl className="grid gap-3 text-xs sm:grid-cols-3">
                    <div>
                      <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle" title="Retrato de agora: pessoas dentro da régua deste funil esperando o próximo toque.">
                        Na régua agora
                      </dt>
                      <dd className="mt-0.5 text-sm font-semibold tabular-nums text-ink">
                        {formatCountCompact(funil.queuedLeads)} <span className="font-normal text-ink-muted">{funil.queuedLeads === 1 ? 'pessoa' : 'pessoas'}</span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle" title="Soma do valor dos carrinhos ou planos de quem está na régua agora.">
                        Parado na régua
                      </dt>
                      <dd className="mt-0.5 text-sm font-semibold tabular-nums text-ink">{formatBRLFromCents(funil.queuedAmountCents)}</dd>
                    </div>
                    <div>
                      <dt
                        className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle"
                        title="Vendas creditadas a este funil nos últimos 30 dias (ADR-001: pagamento em até 7 dias depois de um contato, creditado ao último contato)."
                      >
                        Recuperado em 30 dias
                      </dt>
                      <dd className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-sm font-semibold tabular-nums text-ink">
                        <span>
                          {formatBRLFromCents(funil.last30Days.recoveredAmountCents)}{' '}
                          <span className="font-normal text-ink-muted">· {formatCount(funil.last30Days.recoveredCount)} vendas</span>
                        </span>
                        <span className="text-xs font-normal">
                          <VariacaoTexto atual={funil.last30Days.recoveredAmountCents} anterior={funil.previous30Days.recoveredAmountCents} />
                        </span>
                      </dd>
                    </div>
                  </dl>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-ink-subtle">
                      <span>Alimentado por:</span>
                      {info.providers.map((provider) => {
                        const conectado = provedoresConectados.includes(provider);
                        return (
                          <Badge key={provider} tone={conectado ? 'good' : 'neutral'} dot>
                            {WEBHOOK_PROVIDER_LABEL[provider]}
                            {conectado ? '' : ' · não conectado'}
                          </Badge>
                        );
                      })}
                    </div>
                    <LinkButton to={info.route} size="sm" icon="grafico">
                      Abrir funil
                    </LinkButton>
                  </div>
                </Panel>
              </li>
            );
          })}
        </ul>
      </SectionCard>

      <SectionCard
        id="regras-fixas"
        title="Regras do motor"
        icon="escudo"
        description="Três regras que a empresa não altera por aqui: duas são do produto, a terceira é do contrato. Estão nesta tela para ninguém procurar o campo."
      >
        <div className="grid gap-4 md:grid-cols-3">
          <RegraFixa
            icone="relogio"
            titulo="Espera antes do primeiro toque"
            selo="Fixo · RF04"
            valor={String(draft.waitMinutes)}
            unidade="minutos"
            texto="A régua só começa depois de 30 minutos sem pagamento. Quem paga nesse intervalo nunca recebe contato — e não gera comissão. Encurtar isso incomodaria quem só estava terminando de pagar."
          />
          <RegraFixa
            icone="telefone"
            titulo="Teto de ligações por lead"
            selo="Fixo · RF08"
            valor={String(draft.maxVoiceAttempts)}
            unidade="ligações"
            texto="No máximo duas tentativas da IA por lead. Cada ligação tem custo por minuto no provedor de voz; o teto evita insistir com quem não atende. Depois da segunda, a régua segue pelo WhatsApp."
          />
          <RegraFixa
            icone="comissao"
            titulo="Comissão e estorno"
            selo="Contrato · ADR-001"
            valor={formatPercentFromBps(company.feeBps)}
            unidade="da venda recuperada"
            texto={`Conta se o pagamento chegar em até 7 dias depois de um contato (ligação que chegou ao telefone ou WhatsApp enviado), creditado ao último contato. ${REFUND_POLICY_INFO[refundPolicy].description}`}
          />
        </div>
        <div className="mt-4">
          <Footnote>
            Política de estorno desta empresa: <span className="font-medium text-ink">{REFUND_POLICY_INFO[refundPolicy].label}</span>. Alterada só pela administração, por
            contrato. O modelo de cobrança da comissão (contrato x automático) ainda está a definir.
          </Footnote>
        </div>
      </SectionCard>

      <SectionCard
        id="horario"
        title="Horário permitido"
        icon="calendario"
        description="Ligações e WhatsApp só saem dentro desta faixa. Fora dela, o toque espera a próxima janela — ninguém é pulado."
        aside={
          janela.minutosPorDia !== null && janela.weekdays === null ? (
            <Badge tone="info" icon="relogio">
              {formatarHoras(janela.minutosPorDia)} por dia · {formatCount(diasEscolhidos)} {diasEscolhidos === 1 ? 'dia' : 'dias'} · {formatarHoras(janela.minutosPorDia * diasEscolhidos)} por
              semana
            </Badge>
          ) : undefined
        }
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[9rem_9rem_minmax(0,1fr)]">
          <Field label="Início" required htmlFor={ID_INICIO} error={erroVisivel(janela.start)}>
            {({ id, describedBy, invalid }) => (
              <Input id={id} type="time" step={900} aria-describedby={describedBy} aria-invalid={invalid} value={draft.contactWindow.start} onChange={(event) => definirJanela({ start: event.target.value })} />
            )}
          </Field>
          <Field label="Fim" required htmlFor={ID_FIM} error={erroVisivel(janela.end)}>
            {({ id, describedBy, invalid }) => (
              <Input id={id} type="time" step={900} aria-describedby={describedBy} aria-invalid={invalid} value={draft.contactWindow.end} onChange={(event) => definirJanela({ end: event.target.value })} />
            )}
          </Field>
          <div className="flex flex-col gap-1.5 sm:col-span-2 lg:col-span-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span id={`${ID_DIAS}-rotulo`} className="text-sm font-medium text-ink">
                Dias da semana
              </span>
              <span className="flex items-center gap-1">
                <Button size="sm" variant="ghost" disabled={mesmosDias(draft.contactWindow.weekdays, DIAS_UTEIS)} onClick={() => definirJanela({ weekdays: DIAS_UTEIS })}>
                  Dias úteis
                </Button>
                <Button size="sm" variant="ghost" disabled={mesmosDias(draft.contactWindow.weekdays, TODOS_OS_DIAS)} onClick={() => definirJanela({ weekdays: TODOS_OS_DIAS })}>
                  Todos os dias
                </Button>
              </span>
            </div>
            <div
              id={ID_DIAS}
              tabIndex={-1}
              role="group"
              aria-labelledby={`${ID_DIAS}-rotulo`}
              aria-describedby={tentouSalvar && janela.weekdays !== null ? `${ID_DIAS}-erro` : undefined}
              className="grid grid-cols-2 gap-2 rounded-xl sm:grid-cols-4 lg:grid-cols-7"
            >
              {WEEKDAYS.map((dia) => (
                <Checkbox
                  key={dia.value}
                  checked={draft.contactWindow.weekdays.includes(dia.value)}
                  onChange={(v) => definirDia(dia.value, v)}
                  label={
                    <>
                      <span className="sr-only">{dia.label}</span>
                      <span aria-hidden="true">{dia.short}</span>
                    </>
                  }
                />
              ))}
            </div>
            {tentouSalvar && janela.weekdays !== null ? (
              <p id={`${ID_DIAS}-erro`} role="alert" className="text-xs text-status-critical">
                {janela.weekdays}
              </p>
            ) : null}
          </div>
        </div>
        {janelaCurta ? (
          <p className="mt-3 flex items-start gap-2 text-xs text-ink-muted">
            <Icon name="alerta" size="xs" className="mt-0.5 shrink-0 text-status-warning" />
            <span>Janela curta: com menos de 4 horas por dia, toques como T+1h costumam esperar até o dia seguinte. Não bloqueia salvar, mas atrasa a régua — e o lead esfria.</span>
          </p>
        ) : null}
        <div className="mt-4">
          <Footnote>Fuso da empresa: America/Sao_Paulo. A espera de 30 minutos conta mesmo fora do horário; o que espera é o disparo.</Footnote>
        </div>
      </SectionCard>

      <SectionCard
        id="toques"
        title="Toques da régua"
        icon="disparo"
        description="Ligue ou desligue cada toque. O texto das mensagens e o roteiro da ligação são editados na tela do funil, com a prévia do aparelho."
      >
        <div className="flex flex-col gap-6">
          <Panel tone="brand" className="flex items-start gap-3 px-4 py-3">
            <span aria-hidden="true" className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-brand/10 text-brand">
              <Icon name="escudo" size="sm" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink">Funil só depois do fato</p>
              <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">{NOTA_DEPOIS_DO_FATO}</p>
            </div>
          </Panel>

          {draft.funnels.map((funil) => {
            const info = FUNIL_INFO[funil.kind];
            const fato = FATO_DO_FUNIL[funil.kind];
            const tituloId = `toques-${funil.kind}-titulo`;
            const toquesLigados = funil.touches.filter((t) => t.enabled).length;
            return (
              <section key={funil.kind} aria-labelledby={tituloId} className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 id={tituloId} className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">
                    <Icon name={info.icon} size="sm" />
                    {info.label}
                    <Badge tone="neutral">
                      {formatCount(toquesLigados)} de {formatCount(funil.touches.length)} toques ligados
                    </Badge>
                    {funil.enabled ? null : <Badge tone="warning">Funil desligado</Badge>}
                  </h3>
                  <LinkButton to={info.route} size="sm" icon="editar">
                    Editar copy no funil
                  </LinkButton>
                </div>
                <ol className="divide-y divide-line rounded-xl border border-line bg-elevated">
                  <li className="flex flex-wrap items-start gap-3 px-4 py-3">
                    <span aria-hidden="true" className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand text-white">
                      <Icon name={info.icon} size="xs" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
                        <span className="tabular-nums">{fato.marcoZeroRotulo}</span>
                        {fato.marcoZero}
                        <Badge tone="brand">O fato</Badge>
                      </p>
                      <p className="mt-0.5 text-xs text-ink-subtle">{fato.antesDoFato}</p>
                    </div>
                  </li>
                  {funil.touches.map((toque) => {
                    const foraDaRegra = antesDoFato(funil.kind, toque);
                    const proximo = proximoLigado(funil, toque);
                    const idToque = `cfg-toque-${funil.kind}-${toque.id}`;
                    return (
                      <li key={toque.id} id={idToque} tabIndex={-1} className="flex flex-col gap-2 px-4 py-3">
                        <Toggle
                          checked={toque.enabled && !foraDaRegra}
                          disabled={!funil.enabled || foraDaRegra}
                          onChange={(v) => definirToque(funil.kind, toque.id, v)}
                          label={
                            <span className="flex flex-wrap items-center gap-2">
                              <span className="tabular-nums">{rotuloDoAtraso(funil.kind, toque.delayMinutes)}</span>
                              <Badge tone={toque.channel === 'voice' ? 'accent' : 'info'} icon={toque.channel === 'voice' ? 'telefone' : 'whatsapp'}>
                                {CANAL_LABEL[toque.channel]}
                              </Badge>
                              {toque.withEmail ? (
                                <Badge tone="brand" icon="email">
                                  + e-mail
                                </Badge>
                              ) : null}
                              {toque.isLast ? <Badge tone="neutral">Último aviso</Badge> : null}
                              {foraDaRegra ? (
                                <Badge tone="critical" icon="alerta">
                                  Antes do fato — bloqueado
                                </Badge>
                              ) : null}
                            </span>
                          }
                          description={
                            <>
                              {toque.order}º toque · {descricaoDoAtraso(funil.kind, toque.delayMinutes)}
                              {funil.enabled && toque.waitingNow > 0 ? (
                                <>
                                  {' '}
                                  · <span className="font-medium tabular-nums text-ink">{formatCountCompact(toque.waitingNow)}</span> esperando este toque agora
                                </>
                              ) : null}
                            </>
                          }
                        />
                        {funil.enabled && !toque.enabled && toque.waitingNow > 0 ? (
                          <p role="status" className="flex items-start gap-2 rounded-lg border border-status-warning/50 bg-status-warning/5 px-3 py-2 text-xs text-ink-muted">
                            <Icon name="alerta" size="xs" className="mt-0.5 shrink-0 text-status-warning" />
                            <span>
                              <span className="font-medium tabular-nums text-ink">{formatCount(toque.waitingNow)}</span> {toque.waitingNow === 1 ? 'pessoa espera' : 'pessoas esperam'} este
                              toque. Ao salvar,{' '}
                              {proximo === null ? 'saem da régua sem mais nenhum contato' : `seguem direto para ${rotuloDoAtraso(funil.kind, proximo.delayMinutes)} (${CANAL_LABEL[proximo.channel]})`}.
                            </span>
                          </p>
                        ) : null}
                        {foraDaRegra ? (
                          <p role="alert" className="text-xs text-status-critical">
                            Este toque está antes de {fato.texto} depois do {fato.nome}: seria lembrete para quem ainda não saiu. Ajuste o atraso na tela do funil.
                          </p>
                        ) : null}
                      </li>
                    );
                  })}
                </ol>
              </section>
            );
          })}
        </div>
      </SectionCard>

      <Panel className="px-5 py-4">
        <BarraSalvar
          bordered={false}
          dirty={rascunho.dirty}
          saved={rascunho.saved}
          onSave={salvar}
          onDiscard={descartar}
          bloqueado={tentouSalvar && temErro}
          motivoBloqueio="Revise o horário permitido."
          erros={erros}
        />
      </Panel>

      <Modal
        open={desligando !== null}
        onClose={() => setDesligando(null)}
        title={desligando === null ? 'Desligar funil' : `Desligar o funil ${FUNIL_INFO[desligando.kind].label}?`}
        description="Os toques param na hora para quem está na régua. Quem sair agora não volta quando o funil religar; só entradas novas."
        footer={
          <>
            <Button variant="ghost" onClick={() => setDesligando(null)}>
              Manter ligado
            </Button>
            <Button variant="danger" icon="pausa" onClick={confirmarDesligar}>
              Desligar mesmo assim
            </Button>
          </>
        }
      >
        {desligando === null ? null : (
          <div className="flex flex-col gap-2">
            <MetricLine
              icon="leads"
              value={formatCount(desligando.queuedLeads)}
              label={desligando.queuedLeads === 1 ? 'pessoa na régua agora deixaria de ser contatada' : 'pessoas na régua agora deixariam de ser contatadas'}
              tone={desligando.queuedLeads > 0 ? 'critical' : 'neutral'}
              muted={desligando.queuedLeads === 0}
            />
            <MetricLine
              icon="moeda"
              value={formatBRLFromCents(desligando.queuedAmountCents)}
              label={desligando.kind === 'expirados' ? 'em planos vencidos parados nessa fila' : 'em carrinhos parados nessa fila'}
              tone={desligando.queuedAmountCents > 0 ? 'warning' : 'neutral'}
              muted={desligando.queuedAmountCents === 0}
            />
            <MetricLine
              icon="comissao"
              value={formatBRLFromCents(desligando.last30Days.recoveredAmountCents)}
              label="recuperados por este funil nos últimos 30 dias"
              detail={
                <>
                  {formatCount(desligando.last30Days.recoveredCount)} vendas · <VariacaoTexto atual={desligando.last30Days.recoveredAmountCents} anterior={desligando.previous30Days.recoveredAmountCents} />
                </>
              }
              tone={desligando.last30Days.recoveredAmountCents > 0 ? 'good' : 'neutral'}
              muted={desligando.last30Days.recoveredAmountCents === 0}
            />
            <p className="mt-1 text-xs text-ink-subtle">A mudança só vale depois de “Salvar na prévia”. Até lá, dá para voltar atrás com “Descartar”.</p>
          </div>
        )}
      </Modal>
    </div>
  );
}
